<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import {
  getSchedulingPage,
  getAvailability,
} from '../../scheduler/api/scheduling';
import {
  buildUpcomingDays,
  dateKeyInTimezone,
  formatSlotTime,
  formatSlotDateTime,
  addDaysToKey,
} from '../../scheduler/helpers/datetime';

// Escolha de horário DENTRO do formulário. Consome os mesmos endpoints
// públicos do agendador (`/public/api/v1/scheduling/:slug`), então o que vale
// aqui é a mesma disponibilidade que o link da agenda mostra — inclusive a
// agenda por serviço, quando a página está nesse modo.
//
// Quem marca de verdade é o servidor, no envio. Aqui só escolhemos: se o
// horário cair entre a escolha e o envio, o lead continua salvo e a tela de
// conclusão avisa. Perder o lead por causa do horário seria pior.
const props = defineProps({
  field: { type: Object, required: true },
  modelValue: { type: [Object, String], default: null },
});
const emit = defineEmits(['update:modelValue']);

const DIAS_VISIVEIS = 14;

const page = ref(null);
const loading = ref(true);
const erro = ref('');
const slots = ref([]);
const carregandoSlots = ref(false);
const servicoId = ref('');
const diaSelecionado = ref('');

const timezone = computed(
  () => page.value?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
);
const servicos = computed(() => page.value?.services || []);
const modoServico = computed(
  () => Boolean(page.value?.service_mode) && servicos.value.length > 0
);
const servicoEscolhido = computed(() =>
  servicos.value.find(item => item.id === servicoId.value)
);
// Sem serviço escolhido num modo por serviço, nem faz sentido buscar horário:
// a duração muda a grade inteira.
const precisaEscolherServico = computed(
  () => modoServico.value && !servicoEscolhido.value
);
const duracao = computed(
  () => servicoEscolhido.value?.duration_minutes || page.value?.duration_minutes
);

const janela = computed(() =>
  Math.min(page.value?.max_advance_days || DIAS_VISIVEIS, DIAS_VISIVEIS)
);
const dias = computed(() =>
  page.value ? buildUpcomingDays(janela.value, timezone.value) : []
);

const slotsPorDia = computed(() => {
  const mapa = {};
  slots.value.forEach(iso => {
    const chave = dateKeyInTimezone(new Date(iso), timezone.value);
    mapa[chave] = mapa[chave] || [];
    mapa[chave].push(iso);
  });
  return mapa;
});
const slotsDoDia = computed(
  () => slotsPorDia.value[diaSelecionado.value] || []
);
const diasComHorario = computed(() =>
  dias.value.filter(dia => (slotsPorDia.value[dia.key] || []).length > 0)
);

const escolhido = computed(() =>
  typeof props.modelValue === 'string'
    ? props.modelValue
    : props.modelValue?.slot || ''
);

const carregarSlots = async () => {
  if (!page.value || precisaEscolherServico.value) {
    slots.value = [];
    return;
  }
  carregandoSlots.value = true;
  try {
    const hoje = dateKeyInTimezone(new Date(), timezone.value);
    const resposta = await getAvailability({
      slug: props.field.scheduling_slug,
      from: hoje,
      to: addDaysToKey(hoje, janela.value - 1),
      duration: duracao.value,
    });
    slots.value = resposta.slots || [];
    const primeiro = diasComHorario.value[0];
    if (!slotsDoDia.value.length && primeiro)
      diaSelecionado.value = primeiro.key;
  } catch {
    slots.value = [];
  } finally {
    carregandoSlots.value = false;
  }
};

onMounted(async () => {
  try {
    page.value = await getSchedulingPage(props.field.scheduling_slug);
    diaSelecionado.value = dateKeyInTimezone(new Date(), timezone.value);
    await carregarSlots();
  } catch {
    // Agenda fora do ar não pode travar o formulário: o campo some e o resto
    // do preenchimento segue normalmente.
    erro.value = 'indisponivel';
  } finally {
    loading.value = false;
  }
});

watch(servicoId, async () => {
  emit('update:modelValue', null);
  await carregarSlots();
});

const escolherServico = id => {
  servicoId.value = servicoId.value === id ? '' : id;
};

const escolherSlot = iso => {
  const nomeServico = servicoEscolhido.value?.name;
  const quando = formatSlotDateTime(iso, timezone.value);
  emit('update:modelValue', {
    slot: iso,
    service: servicoId.value || null,
    // O rótulo viaja junto porque é ele que aparece na nota da conversa e na
    // planilha, já no fuso da agenda — o servidor não precisa reformatar.
    label: nomeServico ? `${quando} — ${nomeServico}` : quando,
  });
};

const hora = iso => formatSlotTime(iso, timezone.value);
const precoServico = item =>
  item.price ? ` · R$ ${Number(item.price).toFixed(2).replace('.', ',')}` : '';
</script>

<template>
  <div class="csf-sched">
    <p v-if="loading && !erro" class="csf-sched__msg">Carregando horários…</p>

    <template v-else-if="!erro">
      <div v-if="modoServico" class="csf-sched__servicos">
        <button
          v-for="item in servicos"
          :key="item.id"
          type="button"
          class="csf-sched__servico"
          :class="{ 'csf-sched__servico--on': item.id === servicoId }"
          @click="escolherServico(item.id)"
        >
          <span class="csf-sched__servico-nome">{{ item.name }}</span>
          <span class="csf-sched__servico-info">
            {{ item.duration_minutes }} min{{ precoServico(item) }}
          </span>
        </button>
      </div>

      <p v-if="precisaEscolherServico" class="csf-sched__msg">
        Escolha o serviço para ver os horários.
      </p>

      <template v-else>
        <div v-if="diasComHorario.length" class="csf-sched__dias">
          <button
            v-for="dia in diasComHorario"
            :key="dia.key"
            type="button"
            class="csf-sched__dia"
            :class="{ 'csf-sched__dia--on': dia.key === diaSelecionado }"
            @click="diaSelecionado = dia.key"
          >
            <span class="csf-sched__dia-semana">{{ dia.weekday }}</span>
            <span class="csf-sched__dia-numero">{{ dia.dayMonth }}</span>
          </button>
        </div>

        <p v-if="carregandoSlots" class="csf-sched__msg">Buscando horários…</p>
        <p v-else-if="!diasComHorario.length" class="csf-sched__msg">
          Sem horários livres nos próximos dias. Envie o formulário que entramos
          em contato.
        </p>

        <div v-if="slotsDoDia.length" class="csf-sched__horas">
          <button
            v-for="iso in slotsDoDia"
            :key="iso"
            type="button"
            class="csf-sched__hora"
            :class="{ 'csf-sched__hora--on': iso === escolhido }"
            @click="escolherSlot(iso)"
          >
            {{ hora(iso) }}
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.csf-sched {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.csf-sched__msg {
  margin: 0;
  font-size: 13px;
  opacity: 0.75;
}

.csf-sched__servicos {
  display: grid;
  gap: 8px;
}

.csf-sched__servico {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  padding: 11px 14px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: var(--csf-radius);
  cursor: pointer;
  font-family: var(--csf-font);
  color: var(--csf-text);
}

.csf-sched__servico--on {
  border-color: var(--csf-primary);
  box-shadow: 0 0 0 1px var(--csf-primary);
}

.csf-sched__servico-nome {
  font-size: 15px;
  font-weight: 600;
}

.csf-sched__servico-info {
  font-size: 12px;
  opacity: 0.7;
}

.csf-sched__dias {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.csf-sched__dia {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 64px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: var(--csf-radius);
  cursor: pointer;
  font-family: var(--csf-font);
  color: var(--csf-text);
}

.csf-sched__dia--on {
  border-color: var(--csf-primary);
  box-shadow: 0 0 0 1px var(--csf-primary);
}

.csf-sched__dia-semana {
  font-size: 11px;
  text-transform: uppercase;
  opacity: 0.7;
}

.csf-sched__dia-numero {
  font-size: 14px;
  font-weight: 600;
}

.csf-sched__horas {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
}

.csf-sched__hora {
  padding: 10px 6px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: var(--csf-radius);
  cursor: pointer;
  font-family: var(--csf-font);
  font-size: 14px;
  color: var(--csf-text);
}

.csf-sched__hora--on {
  background: var(--csf-primary);
  border-color: var(--csf-primary);
  color: #fff;
}
</style>
